import { showToast } from '../components/Toast';

// Detect Capacitor native environment — serve API from remote, assets are local
const isNative = typeof (window as any)?.Capacitor?.isNativePlatform === 'function'
  && (window as any).Capacitor.isNativePlatform();
const REMOTE_HOST = 'https://track.lalatendu.info';
const API_BASE = isNative ? `${REMOTE_HOST}/api` : '/api';

function getToken(): string | null {
  return localStorage.getItem('token');
}

// --- Stale-while-revalidate cache for GET requests ---
const CACHE_TTL: Record<string, number> = {
  '/reports/dashboard': 5 * 60_000,       // 5 min
  '/buses': 10 * 60_000,                  // 10 min
  '/routes': 30 * 60_000,                 // 30 min
  '/routes/all-stops': 30 * 60_000,
  '/students': 15 * 60_000,              // 15 min
  '/settings/school': 30 * 60_000,
  '/parent/children': 15 * 60_000,
};

function getCacheKey(path: string): string {
  return `api_cache:${path}`;
}

function getFromCache<T>(path: string): T | null {
  const key = getCacheKey(path);
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const { data, ts } = JSON.parse(raw);
    const ttl = CACHE_TTL[path];
    if (ttl && Date.now() - ts < ttl) return data as T;
    return data as T; // return stale data — caller will revalidate
  } catch { return null; }
}

function setCache(path: string, data: unknown): void {
  const key = getCacheKey(path);
  try {
    localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
  } catch { /* quota exceeded — ignore */ }
}

function isCacheFresh(path: string): boolean {
  const key = getCacheKey(path);
  const raw = localStorage.getItem(key);
  if (!raw) return false;
  try {
    const { ts } = JSON.parse(raw);
    const ttl = CACHE_TTL[path];
    return !!ttl && Date.now() - ts < ttl;
  } catch { return false; }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  silent = false
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // Don't set Content-Type for FormData (browser sets it with boundary)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const isGet = !options.method || options.method === 'GET';
  const cacheable = isGet && path in CACHE_TTL;

  // Return fresh cache immediately without network call
  if (cacheable && isCacheFresh(path)) {
    const cached = getFromCache<T>(path);
    if (cached) {
      // Background revalidate if >50% TTL elapsed
      const raw = localStorage.getItem(getCacheKey(path));
      if (raw) {
        const { ts } = JSON.parse(raw);
        const ttl = CACHE_TTL[path]!;
        if (Date.now() - ts > ttl * 0.5) {
          fetch(`${API_BASE}${path}`, { ...options, headers }).then(async (r) => {
            if (r.ok) setCache(path, await r.json());
          }).catch(() => {});
        }
      }
      return cached;
    }
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  } catch {
    // Network failed — return stale cache if available
    if (cacheable) {
      const stale = getFromCache<T>(path);
      if (stale) return stale;
    }
    const msg = 'Network error. Check your connection.';
    if (!silent) showToast(msg);
    throw new Error(msg);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const msg = (body as { error?: string }).error || `HTTP ${res.status}`;

    // Handle expired/invalid token — clear auth and redirect to login
    if (res.status === 401 && path !== '/auth/login') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      showToast('Session expired. Please log in again.');
      // Small delay so toast is visible before redirect
      setTimeout(() => { window.location.href = '/'; }, 500);
    } else if (!silent && res.status !== 401) {
      showToast(msg);
    }
    throw new Error(msg);
  }

  const data = await res.json() as T;

  // Cache successful GET responses
  if (cacheable) setCache(path, data);

  return data;
}

export const api = {
  // Auth
  login: (email: string, password: string) =>
    request<{ token: string; user: import('./types').User }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  signup: (data: { school_name: string; admin_name: string; admin_email: string; admin_password: string; admin_phone?: string }) =>
    request<{ ok: boolean; message: string }>('/auth/signup', { method: 'POST', body: JSON.stringify(data) }, true),

  me: () => request<{ user: import('./types').User }>('/auth/me'),

  register: (data: { email: string; password: string; name: string; phone?: string; role: string }) =>
    request('/auth/register', { method: 'POST', body: JSON.stringify(data) }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }),

  // Buses
  getBuses: () =>
    request<{ buses: import('./types').Bus[] }>('/buses'),

  getBus: (id: string) =>
    request<{
      bus: import('./types').Bus;
      route: import('./types').Route | null;
      stops: import('./types').Stop[] | null;
    }>(`/buses/${id}`),

  // Routes
  getRoutes: () =>
    request<{ routes: import('./types').Route[] }>('/routes'),

  getRoute: (id: string) =>
    request<{ route: import('./types').Route; stops: import('./types').Stop[] }>(`/routes/${id}`),
  getStops: () =>
    request<{ stops: import('./types').Stop[] }>('/routes/all-stops'),

  // Students
  getStudents: () =>
    request<{ students: import('./types').Student[] }>('/students'),

  // Admin CRUD
  createBus: (data: Record<string, unknown>) =>
    request('/admin/buses', { method: 'POST', body: JSON.stringify(data) }),

  updateBus: (id: string, data: Record<string, unknown>) =>
    request(`/admin/buses/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  createRoute: (data: Record<string, unknown>) =>
    request('/admin/routes', { method: 'POST', body: JSON.stringify(data) }),

  createStop: (routeId: string, data: Record<string, unknown>) =>
    request(`/admin/routes/${routeId}/stops`, { method: 'POST', body: JSON.stringify(data) }),

  getUsers: (role?: string) =>
    request<{ users: any[] }>(`/admin/users${role ? `?role=${role}` : ''}`),

  createStudent: (data: Record<string, unknown>) =>
    request('/admin/students', { method: 'POST', body: JSON.stringify(data) }),

  updateStudent: (id: string, data: Record<string, unknown>) =>
    request(`/admin/students/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteStudent: (id: string) =>
    request(`/admin/students/${id}`, { method: 'DELETE' }),
  uploadStudentPhoto: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('photo', file);
    return request<{ ok: boolean; photo_url: string }>(`/admin/students/${id}/photo`, { method: 'POST', body: formData });
  },
  updateUser: (id: string, data: Record<string, unknown>) =>
    request(`/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  assignBusRoute: (data: { bus_id: string; route_id: string; days: number[] }) =>
    request('/admin/bus-routes', { method: 'POST', body: JSON.stringify(data) }),

  // Roles & RBAC
  getRoles: () => request<{ roles: import('./types').Role[] }>('/roles'),
  getRolePermissions: (id: string) => request<{ permissions: import('./types').Permission[] }>(`/roles/${id}/permissions`),
  getAllPermissions: () => request<{ permissions: import('./types').Permission[] }>('/roles/permissions/all'),
  getUserRoles: (userId: string) => request<{ roles: import('./types').Role[] }>(`/roles/user/${userId}`),
  assignRole: (userId: string, roleId: string) =>
    request('/roles/assign', { method: 'POST', body: JSON.stringify({ user_id: userId, role_id: roleId }) }),
  removeRole: (userId: string, roleId: string) =>
    request('/roles/assign', { method: 'DELETE', body: JSON.stringify({ user_id: userId, role_id: roleId }) }),

  // Dashboard
  getDashboardStats: () => request<{ stats: import('./types').DashboardStats }>('/reports/dashboard'),

  // Alerts
  getAlerts: (params?: { status?: string; type?: string }) => {
    const qs = new URLSearchParams(params as Record<string, string>).toString();
    return request<{ alerts: import('./types').Alert[] }>(`/safety/alerts${qs ? `?${qs}` : ''}`);
  },
  resolveAlert: (id: number) =>
    request(`/safety/alerts/${id}/resolve`, { method: 'PUT' }),

  // Fleet
  getMaintenance: (busId?: string) =>
    request<{ maintenance: import('./types').Maintenance[] }>(`/fleet/maintenance${busId ? `?bus_id=${busId}` : ''}`),
  createMaintenance: (data: Record<string, unknown>) =>
    request('/fleet/maintenance', { method: 'POST', body: JSON.stringify(data) }),
  updateMaintenance: (id: string, data: Record<string, unknown>) =>
    request(`/fleet/maintenance/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  getDueMaintenance: () =>
    request<{ due: import('./types').Maintenance[] }>('/fleet/maintenance/due'),
  getDocuments: (busId?: string) =>
    request<{ documents: import('./types').VehicleDocument[] }>(`/fleet/documents${busId ? `?bus_id=${busId}` : ''}`),
  uploadDocument: (formData: FormData) =>
    request<{ document: import('./types').VehicleDocument }>('/fleet/documents/upload', {
      method: 'POST',
      body: formData,
    }),
  deleteDocument: (id: string) =>
    request(`/fleet/documents/${id}`, { method: 'DELETE' }),
  getInspections: (busId?: string) =>
    request<{ inspections: import('./types').Inspection[] }>(`/fleet/inspections${busId ? `?bus_id=${busId}` : ''}`),
  createInspection: (data: Record<string, unknown>) =>
    request('/fleet/inspections', { method: 'POST', body: JSON.stringify(data) }),
  getFuelLogs: (busId?: string) =>
    request<{ fuel_logs: import('./types').FuelLog[] }>(`/fleet/fuel${busId ? `?bus_id=${busId}` : ''}`),
  createFuelLog: (data: Record<string, unknown>) =>
    request('/fleet/fuel', { method: 'POST', body: JSON.stringify(data) }),
  getFleetHealth: () =>
    request<{ fleet_health: Record<string, number> }>('/fleet/health'),

  // Safety
  sendSOS: (data: { bus_id: string; latitude?: number; longitude?: number; message?: string }) =>
    request('/safety/sos', { method: 'POST', body: JSON.stringify(data) }),

  reportHazard: (data: { bus_id: string; hazard_type: string; message?: string; latitude?: number; longitude?: number }) =>
    request('/safety/hazard', { method: 'POST', body: JSON.stringify(data) }),
  getSOSAlerts: (status?: string) =>
    request<{ sos_alerts: import('./types').SOSAlert[] }>(`/safety/sos${status ? `?status=${status}` : ''}`),
  acknowledgesSOS: (id: string) =>
    request(`/safety/sos/${id}/acknowledge`, { method: 'PUT' }),
  resolveSOS: (id: string) =>
    request(`/safety/sos/${id}/resolve`, { method: 'PUT' }),
  getSpeedViolations: (params?: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return request<{ speed_violations: import('./types').SpeedViolation[] }>(`/safety/speed-violations${qs ? `?${qs}` : ''}`);
  },

  // Trips
  getTodayTrips: () => request<{ trips: import('./types').TripLog[] }>('/trips/today'),
  startTrip: (data: { bus_id: string; route_id: string; type?: string }) =>
    request('/trips', { method: 'POST', body: JSON.stringify(data) }),
  completeTrip: (id: string, data?: { actual_students?: number; notes?: string }) =>
    request(`/trips/${id}/complete`, { method: 'PUT', body: JSON.stringify(data || {}) }),
  getDriverSheet: () =>
    request<{ bus: import('./types').Bus; route: any; stops: import('./types').Stop[]; students: any[] }>('/trips/driver/sheet'),

  // Chat
  getChatChannels: () => request<{ channels: import('./types').ChatChannel[] }>('/chat/channels'),
  getChatMessages: (type: string, id: string, cursor?: string) =>
    request<{ messages: import('./types').ChatMessage[] }>(`/chat/${type}/${id}/messages${cursor ? `?cursor=${cursor}` : ''}`),
  sendChatMessage: (type: string, id: string, content: string) =>
    request(`/chat/${type}/${id}/messages`, { method: 'POST', body: JSON.stringify({ content }) }),
  getAnnouncements: () => request<{ announcements: import('./types').Announcement[] }>('/chat/announcements'),
  createAnnouncement: (data: Record<string, unknown>) =>
    request('/chat/announcements', { method: 'POST', body: JSON.stringify(data) }),
  deleteAnnouncement: (id: string) =>
    request(`/chat/announcements/${id}`, { method: 'DELETE' }),

  // Parent
  getChildren: () => request<{ children: any[] }>('/parent/children'),
  getTripHistory: (studentId?: string, days?: number) => {
    const params = new URLSearchParams();
    if (studentId) params.set('student_id', studentId);
    if (days) params.set('days', String(days));
    return request<{ history: any[] }>(`/parent/trip-history?${params}`);
  },
  submitStopChangeRequest: (data: Record<string, unknown>) =>
    request('/parent/stop-change-request', { method: 'POST', body: JSON.stringify(data) }),
  getStopChangeRequests: () => request<{ requests: import('./types').StopChangeRequest[] }>('/parent/stop-change-requests'),
  submitFeedback: (data: Record<string, unknown>) =>
    request('/parent/feedback', { method: 'POST', body: JSON.stringify(data) }),
  getMyFeedback: () => request<{ feedback: import('./types').Feedback[] }>('/parent/feedback'),
  getEmergencyContacts: () => request<{ contacts: import('./types').EmergencyContact[] }>('/parent/emergency-contacts'),
  createEmergencyContact: (data: Record<string, unknown>) =>
    request('/parent/emergency-contacts', { method: 'POST', body: JSON.stringify(data) }),
  deleteEmergencyContact: (id: string) =>
    request(`/parent/emergency-contacts/${id}`, { method: 'DELETE' }),

  // Cancel-a-Ride
  cancelRide: (data: { student_id: string; date?: string; reason?: string }) =>
    request<{ cancellation: any }>('/parent/cancel-ride', { method: 'POST', body: JSON.stringify(data) }),
  getCancelledRides: () =>
    request<{ cancellations: any[] }>('/parent/cancel-ride'),
  undoCancelRide: (id: string) =>
    request(`/parent/cancel-ride/${id}`, { method: 'DELETE' }),

  // Feedback Admin
  getAllFeedback: (params?: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return request<{ feedback: import('./types').Feedback[] }>(`/feedback${qs ? `?${qs}` : ''}`);
  },
  updateFeedback: (id: string, data: Record<string, unknown>) =>
    request(`/feedback/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  getStopChangeRequestsAdmin: () =>
    request<{ requests: import('./types').StopChangeRequest[] }>('/feedback/stop-change-requests'),
  reviewStopChangeRequest: (id: string, status: string) =>
    request(`/feedback/stop-change-requests/${id}`, { method: 'PUT', body: JSON.stringify({ status }) }),

  // Billing
  getSubscription: () => request<{ subscription: import('./types').Subscription }>('/billing/subscription'),
  getPlans: () => request<{ plans: import('./types').Plan[] }>('/billing/plans'),
  createCheckout: (plan: string) =>
    request<{ checkout_url: string }>('/billing/create-checkout', { method: 'POST', body: JSON.stringify({ plan }) }),
  getPortalUrl: () =>
    request<{ portal_url: string }>('/billing/portal', { method: 'POST' }),
  getInvoices: () => request<{ invoices: import('./types').Invoice[] }>('/billing/invoices'),

  // Reports
  getAttendanceReport: (params?: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return request<{ attendance: any[] }>(`/reports/attendance${qs ? `?${qs}` : ''}`);
  },
  getSpeedReport: (params?: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return request<{ violations: any[] }>(`/reports/speed${qs ? `?${qs}` : ''}`);
  },
  getRidershipReport: (range?: string) =>
    request<{ ridership: any[] }>(`/reports/ridership${range ? `?range=${range}` : ''}`),
  generateReport: (data: { type: string; format: string; date_from: string; date_to: string }) =>
    request('/reports/generate', { method: 'POST', body: JSON.stringify(data) }),
  getGeneratedReports: () => request<{ reports: import('./types').GeneratedReport[] }>('/reports/generated'),
  getReportSchedules: () => request<{ schedules: any[] }>('/reports/schedules'),
  createReportSchedule: (data: Record<string, unknown>) =>
    request('/reports/schedules', { method: 'POST', body: JSON.stringify(data) }),

  // Settings
  getSchoolSettings: () => request<{ settings: import('./types').SchoolSettings }>('/settings/school'),
  updateSchoolSettings: (data: Record<string, unknown>) =>
    request('/settings/school', { method: 'PUT', body: JSON.stringify(data) }),
  getCalendar: () => request<{ calendar: import('./types').CalendarEntry[] }>('/settings/calendar'),
  addCalendarEntry: (data: Record<string, unknown>) =>
    request('/settings/calendar', { method: 'POST', body: JSON.stringify(data) }),
  deleteCalendarEntry: (id: string) =>
    request(`/settings/calendar/${id}`, { method: 'DELETE' }),
  getShifts: () => request<{ shifts: import('./types').ShiftConfig[] }>('/settings/shifts'),
  createShift: (data: Record<string, unknown>) =>
    request('/settings/shifts', { method: 'POST', body: JSON.stringify(data) }),
  updateShift: (id: string, data: Record<string, unknown>) =>
    request(`/settings/shifts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteShift: (id: string) =>
    request(`/settings/shifts/${id}`, { method: 'DELETE' }),
  getNotificationRules: () => request<{ rules: any[] }>('/settings/notification-rules'),
  createNotificationRule: (data: Record<string, unknown>) =>
    request('/settings/notification-rules', { method: 'POST', body: JSON.stringify(data) }),
  deleteNotificationRule: (id: string) =>
    request(`/settings/notification-rules/${id}`, { method: 'DELETE' }),

  // Audit
  getAuditLog: (params?: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return request<{ audit_logs: import('./types').AuditEntry[]; total: number; page: number }>(`/audit${qs ? `?${qs}` : ''}`);
  },

  // Super Admin
  getSchools: () => request<{ schools: any[] }>('/superadmin/schools'),
  onboardSchool: (data: Record<string, unknown>) =>
    request('/superadmin/schools', { method: 'POST', body: JSON.stringify(data) }),
  getPlatformStats: () => request<{ stats: Record<string, number> }>('/superadmin/platform-stats'),
  getFeatureFlags: () => request<{ feature_flags: any[] }>('/superadmin/feature-flags'),
  updateFeatureFlag: (name: string, data: Record<string, unknown>) =>
    request(`/superadmin/feature-flags/${name}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Organization (multi-branch)
  getOrgInfo: () => request<{ org: any; branches: any[] }>('/org/info'),
  getOrgDashboard: () => request<{ stats: any; branches: any[] }>('/org/dashboard'),
  getOrgBuses: () => request<{ buses: any[] }>('/org/buses'),
  getOrgStudents: () => request<{ students: any[] }>('/org/students'),
  createOrg: (data: Record<string, unknown>) =>
    request('/org/create', { method: 'POST', body: JSON.stringify(data) }),
  addBranch: (orgId: string, schoolId: string) =>
    request(`/org/${orgId}/add-branch`, { method: 'POST', body: JSON.stringify({ school_id: schoolId }) }),
  getOrgList: () => request<{ organizations: any[] }>('/org/list'),

  // QR Check-in
  getStudentQRToken: (studentId: string, isStatic = false) =>
    request<{ token: string; student: any; date: string; expires: string }>(`/checkin/qr/token/${studentId}${isStatic ? '?static=true' : ''}`),
  getBusQRToken: () =>
    request<{ token: string; bus: any; trip: any }>('/checkin/qr/bus-token'),
  verifyCheckinQR: (data: { token: string; type: 'board' | 'exit'; latitude?: number; longitude?: number }) =>
    request<{ ok: boolean; valid: boolean; message: string; student: any }>('/checkin/verify', { method: 'POST', body: JSON.stringify(data) }),
  parentVerifyBusQR: (token: string) =>
    request<{ valid: boolean; bus: any; child_on_bus: any; trip_active: boolean }>('/checkin/parent-verify', { method: 'POST', body: JSON.stringify({ token }) }),
  getCheckinHistory: (busId: string) =>
    request<{ checkins: any[] }>(`/checkin/history/${busId}`),
  getStudentCard: (studentId: string) =>
    request<{ student: any; school: any; qr_token: string }>(`/checkin/card/${studentId}`),

  // Licensing
  getLicenseStatus: () => request<any>('/license/status'),
  activateLicenseKey: (data: { license_key: string; org_id?: string; school_id?: string }) =>
    request('/license/activate', { method: 'POST', body: JSON.stringify(data) }),
  checkFeature: (feature: string) =>
    request<{ allowed: boolean; plan: string; feature: string }>('/license/check-feature', { method: 'POST', body: JSON.stringify({ feature }) }),

  // License Admin
  issueLicense: (data: Record<string, unknown>) =>
    request('/license/admin/issue', { method: 'POST', body: JSON.stringify(data) }),
  getLicenses: (params?: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return request<{ licenses: any[] }>(`/license/admin/list${qs ? `?${qs}` : ''}`);
  },
  getLicenseDetail: (id: string) => request<any>(`/license/admin/${id}`),
  suspendLicense: (id: string) =>
    request(`/license/admin/${id}/suspend`, { method: 'PUT' }),
  revokeLicense: (id: string) =>
    request(`/license/admin/${id}/revoke`, { method: 'PUT' }),
  reactivateLicense: (id: string) =>
    request(`/license/admin/${id}/reactivate`, { method: 'PUT' }),
  extendLicense: (id: string, days: number) =>
    request(`/license/admin/${id}/extend`, { method: 'PUT', body: JSON.stringify({ days }) }),
  getLicensePlans: () => request<{ plans: any; features: string[] }>('/license/admin/plans/all'),
};
